import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './GasDistributorNewClientCard.css';
import AppIcon from './icons/AppIcon';

export default function GasDistributorNewClientCard({ 
  card, 
  onClose 
}) {
  const [contact, setContact] = useState(null);
  const [connection, setConnection] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info'); // 'info' ou 'messages'

  useEffect(() => {
    if (card.entity_type === 'gas_distributor_new_client' && card.entity_id) {
      loadContactData();
    }
  }, [card.entity_id]);

  const loadContactData = async () => {
    try {
      setLoading(true);

      // Buscar dados do contato
      const { data: contactData, error: contactError } = await supabase
        .from('contatos')
        .select('*')
        .eq('id', card.entity_id)
        .single();

      if (contactError) throw contactError;
      setContact(contactData);

      // Buscar conversas do contato
      const { data: conversasData } = await supabase
        .from('conversas')
        .select('*')
        .eq('contato_id', card.entity_id)
        .order('atualizado_em', { ascending: false });

      setConversations(conversasData || []);

      // Buscar dados da conexão
      if (contactData?.connection_id) {
        const { data: connectionData } = await supabase
          .from('connections')
          .select('name, type')
          .eq('id', contactData.connection_id)
          .single();

        setConnection(connectionData);
      }

      // Selecionar primeira conversa e carregar mensagens
      if (conversasData && conversasData.length > 0) {
        setSelectedConversation(conversasData[0]);
        loadMessages(conversasData[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do contato:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const { data: messagesData, error } = await supabase
        .from('mensagens')
        .select('id, conteudo, tipo_conteudo, direcao, from_me, criado_em, url_midia, transcricao, metadados')
        .eq('conversa_id', conversationId)
        .order('criado_em', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Inverter para mostrar do mais antigo ao mais recente
      setMessages((messagesData || []).reverse());
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const withoutCountry = cleaned.startsWith('55') ? cleaned.slice(2) : cleaned;
    if (withoutCountry.length === 11) {
      return `(${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(2, 7)}-${withoutCountry.slice(7)}`;
    }
    if (withoutCountry.length === 10) {
      return `(${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(2, 6)}-${withoutCountry.slice(6)}`;
    }
    return phone;
  };

  const formatTimestamp = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('pt-BR');
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `Há ${diffMins}m`;
    if (diffHours < 24) return `Há ${diffHours}h`;
    if (diffDays < 7) return `Há ${diffDays}d`;
    return formatTimestamp(date);
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="gas-new-client-modal loading" onClick={(e) => e.stopPropagation()}>
          <div className="loading-spinner">
            <AppIcon name="progress_activity" className="spinning" />
            <p>Carregando dados do cliente...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="gas-new-client-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="contact-header-info">
            <div className="contact-avatar">
              {contact?.image_url ? (
                <img src={contact.image_url} alt={contact.nome || 'Contato'} />
              ) : (
                <AppIcon name="person" />
              )}
            </div>
            <div className="contact-details">
              <h2>{contact?.nome || card.title || 'Novo Cliente'}</h2>
              <div className="contact-badges">
                <span className="badge new-client">
                  <AppIcon name="new_label" />
                  Novo Cliente
                </span>
                {contact?.canal && (
                  <span className="badge whatsapp">
                    <AppIcon name="chat" />
                    {contact.canal === 'whatsapp' ? 'WhatsApp' : contact.canal}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="contact-header-actions">
            <div className="contact-phone-inline">
              <img
                src="/assets/images/icones/whatsapp-logo-whatsapp-icon-whatsapp-transparent-free-png.webp"
                alt="WhatsApp"
                className="contact-phone-icon"
              />
              {formatPhoneNumber(contact?.numero)}
            </div>
            <button className="btn-close" onClick={onClose}>
              <AppIcon name="close" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button 
            className={`tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <AppIcon name="info" />
            Informações de Chegada
          </button>
          <button 
            className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <AppIcon name="chat" />
            Mensagens ({messages.length})
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Tab: Informações de Chegada */}
          {activeTab === 'info' && (
            <div className="tab-content">
              <div className="info-section">
                <h3>Informações de Chegada</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <AppIcon name="phone" />
                    <div>
                      <label>Telefone</label>
                      <p>{formatPhoneNumber(contact?.numero)}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="schedule" />
                    <div>
                      <label>Data de Chegada</label>
                      <p>{formatTimestamp(card.created_at)}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="timer" />
                    <div>
                      <label>Tempo desde chegada</label>
                      <p>{getTimeAgo(card.created_at)}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="link" />
                    <div>
                      <label>Canal</label>
                      <p>{contact?.canal === 'whatsapp' ? 'WhatsApp' : contact?.canal || 'Desconhecido'}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="router" />
                    <div>
                      <label>Conexão</label>
                      <p>{connection?.name || 'Não identificada'}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="location_on" />
                    <div>
                      <label>Localização</label>
                      <p>Desconhecida</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Mensagens */}
          {activeTab === 'messages' && (
            <div className="tab-content messages-tab">
              {/* Seletor de conversa se houver múltiplas */}
              {conversations.length > 1 && (
                <div className="conversation-selector">
                  <label>Conversa:</label>
                  <select 
                    value={selectedConversation?.id || ''} 
                    onChange={(e) => {
                      const conv = conversations.find(c => c.id === e.target.value);
                      setSelectedConversation(conv);
                      if (conv) loadMessages(conv.id);
                    }}
                  >
                    {conversations.map(conv => (
                      <option key={conv.id} value={conv.id}>
                        {conv.canal || 'WhatsApp'} - {formatTimestamp(conv.criado_em)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="messages-container">
                {messages.length === 0 ? (
                  <div className="empty-state">
                    <AppIcon name="chat_bubble_outline" />
                    <p>Nenhuma mensagem encontrada</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div 
                      key={msg.id} 
                      className={`message ${msg.from_me ? 'sent' : 'received'}`}
                    >
                      <div className="message-content">
                        {(() => {
                          const tipo = String(msg.tipo_conteudo || '').toLowerCase();
                          const isAudio = tipo === 'audio' || tipo.startsWith('audio/');
                          const isImage = tipo === 'image' || tipo.startsWith('image/');
                          const telegramMeta = msg?.metadados?.telegram || {};

                          if (isAudio) {
                            const transcription = String(
                              msg.transcricao ||
                              telegramMeta.transcription ||
                              msg.conteudo ||
                              ''
                            ).trim();
                            return (
                              <div className="message-audio">
                                <AppIcon name="mic" />
                                <span>{transcription || 'Áudio'}</span>
                              </div>
                            );
                          }

                          if (isImage) {
                            const caption = String(msg.conteudo || '').trim();
                            const imageDescription = String(telegramMeta.image_description || '').trim();
                            const shouldShowCaption = !!caption && (!imageDescription || caption !== imageDescription);

                            return (
                              <div className="message-image">
                                {msg.url_midia && <img src={msg.url_midia} alt="Imagem" />}
                                {shouldShowCaption && <p>{caption}</p>}
                              </div>
                            );
                          }

                          return <p>{msg.conteudo || '(sem conteúdo)'}</p>;
                        })()}
                        <span className="message-time">{formatTimestamp(msg.criado_em)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {messages.length > 0 && (
                <div className="messages-footer">
                  <small>Mostrando últimas {messages.length} mensagens</small>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
